import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLeads, getProfiles, createLead, updateLead, fullNameLastFirst } from '../lib/supabase';
import { formatDistanceToNow } from 'date-fns';
import DataTable from '../components/DataTable';

const GEORGIA_COUNTIES = ['Appling','Atkinson','Bacon','Baker','Baldwin','Banks','Barrow','Bartow','Ben Hill','Berrien','Bibb','Bleckley','Brantley','Brooks','Bryan','Bulloch','Burke','Butts','Calhoun','Camden','Candler','Carroll','Catoosa','Charlton','Chatham','Chattahoochee','Chattooga','Cherokee','Clarke','Clay','Clayton','Clinch','Cobb','Coffee','Colquitt','Columbia','Cook','Coweta','Crawford','Crisp','Dade','Dawson','Decatur','DeKalb','Dodge','Dooly','Dougherty','Douglas','Early','Echols','Effingham','Elbert','Emanuel','Evans','Fannin','Fayette','Floyd','Forsyth','Franklin','Fulton','Gilmer','Glascock','Glynn','Gordon','Grady','Greene','Gwinnett','Habersham','Hall','Hancock','Haralson','Harris','Hart','Heard','Henry','Houston','Irwin','Jackson','Jasper','Jeff Davis','Jefferson','Jenkins','Johnson','Jones','Lamar','Lanier','Laurens','Lee','Liberty','Lincoln','Long','Lowndes','Lumpkin','McDuffie','McIntosh','Macon','Madison','Marion','Meriwether','Miller','Mitchell','Monroe','Montgomery','Morgan','Murray','Muscogee','Newton','Oconee','Oglethorpe','Paulding','Peach','Pickens','Pierce','Pike','Polk','Pulaski','Putnam','Quitman','Rabun','Randolph','Richmond','Rockdale','Schley','Screven','Seminole','Spalding','Stephens','Stewart','Sumter','Talbot','Taliaferro','Tattnall','Taylor','Telfair','Terrell','Thomas','Tift','Toombs','Towns','Treutlen','Troup','Turner','Twiggs','Union','Upson','Walker','Walton','Ware','Warren','Washington','Wayne','Webster','Wheeler','White','Whitfield','Wilcox','Wilkes','Wilkinson','Worth'];

const ACTIVE_STATUSES = ['New Lead', 'Contacted', 'Interested', 'Follow Up'];
const CLOSED_STATUSES = ['Not Interested', 'Unreachable', 'Duplicate', 'Deceased', 'Ineligible', 'Moved / Outside Service Area', 'Chose Another Provider', 'Other', 'Converted'];
const ALL_STATUSES = [...ACTIVE_STATUSES, ...CLOSED_STATUSES];

const REFERRAL_SOURCES = ['Doctor / Physician', 'Hospital', 'Family / Friend', 'Social Media', 'Website', 'Community Event', 'DFCS / State Agency', 'School', 'Previous Client', 'Other'];

const statusBadge = (status) => {
  if (status === 'New Lead') return { bg: 'var(--zone-marketing-bg)', color: 'var(--zone-marketing)' };
  if (status === 'Contacted') return { bg: 'var(--zone-processing-bg)', color: 'var(--zone-processing)' };
  if (status === 'Interested') return { bg: 'var(--zone-approval-bg)', color: 'var(--zone-approval)' };
  if (status === 'Follow Up') return { bg: 'var(--zone-active-bg)', color: 'var(--zone-active)' };
  if (status === 'Converted') return { bg: 'var(--success-bg)', color: 'var(--success)' };
  return { bg: 'var(--danger-bg)', color: 'var(--text3)' };
};

// Service lines are parallel categories, not a status — neutral for both.
const SERVICE_LINE_OPTS = [
  { key: 'home_health', label: 'Home Health', color: 'var(--text3)', bg: 'var(--bg2)' },
  { key: 'dme',         label: 'DME / Supplies', color: 'var(--text3)', bg: 'var(--bg2)' },
];

const serviceLineBadge = (lines = []) => lines.map(l => SERVICE_LINE_OPTS.find(o => o.key === l)).filter(Boolean);

const EMPTY_FORM = { first_name: '', last_name: '', phone: '', email: '', date_of_birth: '', county: '', referral_source: '', client_type: [], service_lines: ['home_health'], guardian_name: '', guardian_phone: '', notes: '', assigned_to_id: '', assigned_to_name: '' };

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [showClosed, setShowClosed] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [closeModal, setCloseModal] = useState(null); // { lead } | null
  const [closeReason, setCloseReason] = useState('Not Interested');
  const [closeNote, setCloseNote] = useState('');
  const [closeSaving, setCloseSaving] = useState(false);
  const navigate = useNavigate();

  const loadLeads = useCallback(async () => {
    setLoading(true);
    const { data } = await getLeads({ showClosed, status: statusFilter });
    setLeads(data || []);
    setLoading(false);
  }, [showClosed, statusFilter]);

  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { getProfiles().then(({ data }) => setStaff(data || [])); }, []);

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const toggleClientType = (t) => setForm(p => ({
    ...p,
    client_type: p.client_type.includes(t) ? p.client_type.filter(x => x !== t) : [...p.client_type, t],
  }));

  const toggleServiceLine = (key) => setForm(p => ({
    ...p,
    service_lines: p.service_lines.includes(key)
      ? p.service_lines.filter(x => x !== key)
      : [...p.service_lines, key],
  }));

  const handleCloseLead = async () => {
    if (!closeModal) return;
    setCloseSaving(true);
    await updateLead(closeModal.lead.id, { current_status: closeReason, last_activity_note: closeNote || null });
    setCloseSaving(false);
    setCloseModal(null);
    setCloseNote('');
    loadLeads();
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaveError('');
    if (!form.first_name.trim() || !form.last_name.trim()) { setSaveError('First and last name are required.'); return; }
    setSaving(true);
    const staffMember = staff.find(s => s.id === form.assigned_to_id);
    const payload = {
      ...form,
      assigned_to_id: form.assigned_to_id || null,
      assigned_to_name: staffMember?.full_name || null,
      date_of_birth: form.date_of_birth || null,
      client_type: form.client_type.length ? form.client_type : null,
      service_lines: form.service_lines.length ? form.service_lines : ['home_health'],
    };
    delete payload.assigned_to_id_temp;
    const { error } = await createLead(payload);
    setSaving(false);
    if (error) { setSaveError(error.message); return; }
    setShowAddModal(false);
    setForm(EMPTY_FORM);
    loadLeads();
  };

  const counts = {
    total: leads.length,
    new: leads.filter(l => l.current_status === 'New Lead').length,
    active: leads.filter(l => ACTIVE_STATUSES.includes(l.current_status) && l.current_status !== 'New Lead').length,
    converted: leads.filter(l => l.current_status === 'Converted').length,
  };

  // enrich with derived per-row fields once, referenced by multiple columns below
  const rows = leads.map(lead => {
    const lastActivity = lead.last_activity_date
      ? formatDistanceToNow(new Date(lead.last_activity_date), { addSuffix: true })
      : 'Never';
    const isStale = !CLOSED_STATUSES.includes(lead.current_status) &&
      (!lead.last_activity_date || new Date(lead.last_activity_date) < new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
    return { ...lead, _lastActivity: lastActivity, _isStale: isStale };
  });

  const columns = [
    {
      accessorKey: 'lead_id', header: 'ID', size: 80,
      meta: { mono: true, mobile: 'secondary' },
      cell: info => info.getValue(),
    },
    {
      id: 'name', header: 'Name', size: 160,
      meta: { mobile: 'primary' },
      cell: info => {
        const lead = info.row.original;
        return (
          <span className="td-link" onClick={() => navigate(`/leads/${lead.id}`)}>
            {lead.last_name}, {lead.first_name}
          </span>
        );
      },
    },
    {
      accessorKey: 'county', header: 'County', size: 110,
      meta: { mobile: 'secondary' },
      cell: info => info.getValue() || '—',
    },
    {
      id: 'services', header: 'Services', size: 150, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => {
        const opts = serviceLineBadge(info.row.original.service_lines);
        return (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {opts.map(opt => (
              <span key={opt.key} style={{ fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 20, background: opt.bg, color: opt.color, whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{opt.label}</span>
            ))}
            {!opts.length && <span style={{ color: 'var(--text3)', fontSize: 11 }}>—</span>}
          </div>
        );
      },
    },
    {
      accessorKey: 'current_status', header: 'Status', size: 130,
      meta: { mobile: 'primary' },
      cell: info => {
        const badge = statusBadge(info.getValue());
        return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: badge.bg, color: badge.color, fontWeight: 500 }}>{info.getValue()}</span>;
      },
    },
    {
      accessorKey: 'referral_source', header: 'Referral Source', size: 150,
      meta: { mobile: 'hidden' },
      cell: info => <span style={{ fontSize: 12, color: 'var(--text2)' }}>{info.getValue() || '—'}</span>,
    },
    {
      id: 'last_activity', header: 'Last Activity', size: 120,
      meta: { mobile: 'secondary' },
      cell: info => {
        const lead = info.row.original;
        return <span style={{ fontSize: 12, color: lead._isStale ? 'var(--danger)' : 'var(--text2)', whiteSpace: 'nowrap' }}>{lead._lastActivity}</span>;
      },
    },
    {
      id: 'last_note', header: 'Last Note', size: 190,
      meta: { truncate: true, mobile: 'hidden' },
      cell: info => {
        const note = info.row.original.last_activity_note;
        return note ? note.slice(0, 60) + (note.length > 60 ? '...' : '') : '—';
      },
    },
    {
      accessorKey: 'assigned_to_name', header: 'Assigned To', size: 130,
      meta: { mobile: 'hidden' },
      cell: info => <span style={{ fontSize: 12 }}>{info.getValue() || '—'}</span>,
    },
    {
      id: 'actions', header: '', size: 150, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => {
        const lead = info.row.original;
        return (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/leads/${lead.id}`)}>View →</button>
            {!CLOSED_STATUSES.includes(lead.current_status) && lead.current_status !== 'Converted' && (
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)', borderColor: 'var(--danger-border)' }}
                onClick={() => { setCloseModal({ lead }); setCloseReason('Not Interested'); setCloseNote(''); }}>
                Close
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Leads', value: counts.total, color: 'var(--accent)' },
          { label: 'New', value: counts.new, color: 'var(--zone-marketing)' },
          { label: 'In Progress', value: counts.active, color: 'var(--zone-active)' },
          { label: 'Converted', value: counts.converted, color: 'var(--success)' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 120 }}>
            <span style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</span>
            <span style={{ fontSize: 11, color: 'var(--text2)' }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="form-select" style={{ width: 'auto', fontSize: 12 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Active Statuses</option>
          {ACTIVE_STATUSES.map(s => <option key={s}>{s}</option>)}
          {showClosed && CLOSED_STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text2)', cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={showClosed} onChange={e => setShowClosed(e.target.checked)} />
          Show closed
        </label>
        <div style={{ marginLeft: 'auto' }}>
          <button className="btn btn-primary btn-sm" onClick={() => setShowAddModal(true)}>+ Add Lead</button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">
            Leads
            <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 400, marginLeft: 8 }}>({leads.length})</span>
          </div>
        </div>

        <DataTable
          tableId="leads"
          columns={columns}
          data={rows}
          getRowId={l => l.id}
          loading={loading}
          emptyMessage="No leads match your filters."
          maxHeight="calc(100vh - 320px)"
        />
      </div>

      {/* Close Lead Modal */}
      {closeModal && (
        <div className="modal-overlay" onClick={() => setCloseModal(null)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Close Lead</div>
              <button className="modal-close" onClick={() => setCloseModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
                Closing <strong>{closeModal.lead.first_name} {closeModal.lead.last_name}</strong> will move them to closed status. You can reactivate them later if needed.
              </p>
              <div className="form-group">
                <label className="form-label">Reason</label>
                <select className="form-select" value={closeReason} onChange={e => setCloseReason(e.target.value)}>
                  <option>Not Interested</option>
                  <option>Unreachable</option>
                  <option>Duplicate</option>
                  <option>Deceased</option>
                  <option>Ineligible</option>
                  <option>Moved / Outside Service Area</option>
                  <option>Chose Another Provider</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Note (optional)</label>
                <textarea className="form-textarea" rows={2} value={closeNote} onChange={e => setCloseNote(e.target.value)} placeholder="Add a closing note..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setCloseModal(null)}>Cancel</button>
              <button className="btn btn-sm" style={{ background: 'var(--danger)', color: '#fff', border: 'none' }} disabled={closeSaving} onClick={handleCloseLead}>{closeSaving ? 'Closing…' : 'Close Lead'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Lead Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" style={{ maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Add Lead</div>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            <form onSubmit={handleAdd}>
              <div className="modal-body">
                {saveError && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{saveError}</div>}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input className="form-input" value={form.first_name} onChange={e => setField('first_name', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name *</label>
                    <input className="form-input" value={form.last_name} onChange={e => setField('last_name', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-input" type="tel" value={form.phone} onChange={e => setField('phone', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input className="form-input" type="email" value={form.email} onChange={e => setField('email', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date of Birth</label>
                    <input className="form-input" type="date" value={form.date_of_birth} onChange={e => setField('date_of_birth', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">County</label>
                    <select className="form-select" value={form.county} onChange={e => setField('county', e.target.value)}>
                      <option value="">Select county</option>
                      {GEORGIA_COUNTIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Referral Source</label>
                    <select className="form-select" value={form.referral_source} onChange={e => setField('referral_source', e.target.value)}>
                      <option value="">Select source</option>
                      {REFERRAL_SOURCES.map(r => <option key={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Assign To</label>
                    <select className="form-select" value={form.assigned_to_id} onChange={e => setField('assigned_to_id', e.target.value)}>
                      <option value="">Unassigned</option>
                      {staff.map(s => <option key={s.id} value={s.id}>{fullNameLastFirst(s.full_name)}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Guardian Name</label>
                    <input className="form-input" value={form.guardian_name} onChange={e => setField('guardian_name', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Guardian Phone</label>
                    <input className="form-input" type="tel" value={form.guardian_phone} onChange={e => setField('guardian_phone', e.target.value)} />
                  </div>
                </div>
                <div className="form-group" style={{ marginTop: 4 }}>
                  <label className="form-label">Services Requested</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {SERVICE_LINE_OPTS.map(opt => {
                      const active = form.service_lines.includes(opt.key);
                      return (
                        <button key={opt.key} type="button" onClick={() => toggleServiceLine(opt.key)} style={{
                          padding: '5px 16px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                          background: active ? opt.bg : 'var(--bg2)',
                          color: active ? opt.color : 'var(--text3)',
                          border: `1.5px solid ${active ? opt.color : 'var(--border)'}`,
                          transition: 'all 0.15s',
                        }}>
                          {active ? '✓ ' : ''}{opt.label}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 5 }}>Select all services this lead is interested in</div>
                </div>
                <div className="form-group" style={{ marginTop: 4 }}>
                  <label className="form-label">Client Type</label>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {['New', 'Transfer', 'Spanish Speaking Only'].map(t => (
                      <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                        <input type="checkbox" checked={form.client_type.includes(t)} onChange={() => toggleClientType(t)} />
                        {t}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" rows={3} value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="Initial notes about this lead..." />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Add Lead'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
